import { Alert, Box } from "@mui/material";
import { useCallback, useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ConfigurationsContext from "context/configurations/ConfigurationsContext";
import { useGetUserDetailsQuery } from "features/api/userApi";
import type { RootState } from "stores/application.store";
import { useGetOrderStatusQuery } from "./messagesApi";

export interface SubmissionTarget {
	accountId: string;
	productLineId: string;
}
export function useOrderSubmissionState() {
	const [blockedTargets, setBlockedTargets] = useState<Record<string, boolean>>({});
	const updateAvailability = useCallback((key: string, blocked: boolean) => {
		setBlockedTargets((previous) => (previous[key] === blocked ? previous : { ...previous, [key]: blocked }));
	}, []);
	return { orderBlocked: Object.values(blockedTargets).some(Boolean), updateAvailability };
}
function TargetAvailability({
	target,
	onChange
}: {
	target: SubmissionTarget;
	onChange: (key: string, blocked: boolean) => void;
}) {
	const { configurationsData } = useContext(ConfigurationsContext);
	const enabled = configurationsData?.featureFlags?.bannersAndAdsEnabled === true;
	const { data: user } = useGetUserDetailsQuery();
	const impersonation = useSelector((state: RootState) => state.impersonation.impersonationUser);
	const contextKey = JSON.stringify([user?.email, impersonation?.email, impersonation?.accountId]);
	const key = target.accountId + ":" + target.productLineId;
	const { currentData, refetch } = useGetOrderStatusQuery(
		{ ...target, contextKey },
		{
			skip: !enabled,
			pollingInterval: 60_000,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true
		}
	);
	const blocked = enabled && currentData?.canSubmit === false;
	useEffect(() => {
		onChange(key, blocked);
		return () => onChange(key, false);
	}, [key, blocked, onChange]);
	useEffect(() => {
		if (!enabled || !currentData?.nextChangeUtc) return;
		const timer = window.setTimeout(
			() => void refetch(),
			Math.min(2_147_483_647, Math.max(100, Date.parse(currentData.nextChangeUtc) - Date.now() + 50))
		);
		return () => window.clearTimeout(timer);
	}, [currentData?.nextChangeUtc, enabled, refetch]);
	return blocked ? <Alert severity="warning">{currentData?.message}</Alert> : null;
}
export function OrderSubmissionAvailability({
	targets,
	onChange
}: {
	targets: SubmissionTarget[];
	onChange: (key: string, blocked: boolean) => void;
}) {
	const distinct = [
		...new Map(
			targets.filter((t) => t.accountId && t.productLineId).map((t) => [t.accountId + ":" + t.productLineId, t])
		).values()
	];
	return (
		<Box sx={{ width: "100%" }}>
			{distinct.map((target) => (
				<TargetAvailability
					key={target.accountId + ":" + target.productLineId}
					target={target}
					onChange={onChange}
				/>
			))}
		</Box>
	);
}
